import { createSelector } from '@ngrx/store';
import { getSharedFeatureState } from '../reducers';
import { ApplicantDocumentType, CodeValue, County, PATLocation } from '../../models/lookup.models';
import { sort } from '../../Utility/sorter';
import { LookupConstants } from '../../../applicant/constants';

export const IsStateLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isStateLoaded
);

export const State = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.state)
);

export const IsCountyLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isCountyLoaded
);

export const GetCounty = createSelector(
  getSharedFeatureState,
  state => sort<County>(Object.values(state.lookup.county), [{ column: 'name', direction: 'asc' }])
);

export const IsCountryLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isCountryLoaded
);

export const Country = createSelector(
  getSharedFeatureState,
  state => {
    let countries = Object.values(state.lookup.country).filter(c => c.id != LookupConstants.COUNTRY_US_ID);
    let us = Object.values(state.lookup.country).find(c => c.id == LookupConstants.COUNTRY_US_ID);

    countries.unshift(us);
    return countries;
  }
);

export const IsRaceLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isRaceLoaded
);

export const Race = createSelector(
  getSharedFeatureState,
  state => sort(Object.values(state.lookup.race), [{ column: 'name', direction: 'asc' }])
);

export const IsSexLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isSexLoaded
);

export const Sex = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.sex)
);

export const IsPrefixLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isPrefixLoaded
);

export const Prefix = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.prefix)
);

export const IsSuffixLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isSuffixLoaded
);

export const Suffix = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.suffix)
);

export const IsMilitaryDischargeTypeLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isMilitaryDischargeTypeLoaded
);

export const MilitaryDischargeType = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.militaryDischargeType)
);

export const IsJobVacancySourceLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isJobVacancySourceLoaded
);

export const JobVacancySource = createSelector(
  getSharedFeatureState,
  state => sort(Object.values(state.lookup.jobVacancySource), [{ column: 'source', direction: 'asc' }])
);

export const IsVeteranPreferenceClaimCategoryLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isVeteranPreferenceClaimCategoryLoaded
);

export const VeteranPreferenceClaimCategory = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.veteranPreferenceClaimCategory)
);

export const IsUserTypeLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isUserTypeLoaded
);

export const UserType = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.userType)
);

export const IsTroopLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isTroopLoaded
);

export const Troop = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.troop)
);

export const IsApplicantDocumentTypeLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isApplicantDocumentTypeLoaded
);

export const GetApplicantDocumentType = createSelector(
  getSharedFeatureState,
  state => sort<ApplicantDocumentType>(Object.values(state.lookup.applicantDocumentType), [{ column: 'name', direction: 'asc' }])
);

export const GetApplicantDocumentTypeCanBeUploadedByApplicant = createSelector(
  GetApplicantDocumentType,
  applicantDocumentTypes => applicantDocumentTypes.filter(adt => adt.canBeUploadedByApplicant)
);

export const getSortCriteriaPATLocation = createSelector(
  getSharedFeatureState,
  state => {
    return state.lookup.sortCriteriaPATLocation;
  }
);

export const getPATLocations = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.patLocation)
);

export const IsPolygraphDisqualificationReasonLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isPolygraphDisqualificationReasonLoaded
);

export const PolygraphDisqualificationReason = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.polygraphDisqualificationReason)
);

export const IsPsychologicalResultLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isPsychologicalResultLoaded
);

export const PsychologicalResult = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.psychologicalResult)
);

export const IsPATResultLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isPATResultLoaded
);

export const PATResult = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.patResult)
);

//export const getPolygraphLocations = createSelector(
//  getSharedFeatureState,
//  state => Object.values(state.lookup.polygraphLocation)
//);

export const IsRoleLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isRoleLoaded
);

export const Role = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.role)
);

export const IsPermissionLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isPermissionLoaded
);

export const Permission = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.permission)
);

export const IsApplicationTypeLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isApplicationTypeLoaded
);

export const ApplicationType = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.applicationType)
);

export const getSortedPATLocations = createSelector(
  getPATLocations,
  getSortCriteriaPATLocation,
  (patLocations, sortCriteria) => sort<PATLocation>(patLocations, [sortCriteria])
);

export const IsEducationTypeLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isEducationTypeLoaded
);

export const EducationType = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.educationType)
);

export const IsWaiverDocumentLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isWaiverDocumentLoaded
);

export const WaiverDocument = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.waiverDocument)
);

export const IsLawEnforcementTestTypeLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isLawEnforcementTestTypeLoaded
);

export const LawEnforcementTestType = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.lawEnforcementTestTypeResult)
);

export const IsCodeValuesLoaded = createSelector(
  getSharedFeatureState,
  state => state.lookup.isCodeValueLoaded
);

export const CodeValues = createSelector(
  getSharedFeatureState,
  state => Object.values(state.lookup.codeValueList)
);

export const CodeValuesByCode = (id: number) =>
  createSelector(CodeValues, (codeValues) => sort<CodeValue>(codeValues.filter(cv => cv.code.id === id), [{ column: 'sortOrder', direction: 'asc' }])
);
